import api from "@/services/utils/apiInstance";
import type {
  AnalyzeImageRequest,
  AnalyzeImageResponse,
  AnalyzeStreamEvent,
  SubmitPricingChoiceRequest,
  SubmitValidationRequest,
  SubmitManualSelectionRequest,
  SubmitRepairRequest,
} from "@/types/services/analysis/analysis.type";
import type { AxiosProgressEvent } from "axios";
import type { CustomAxiosRequestConfig } from "@/types/services/utils/axios.type.ts";

const parseSseEvents = (
  buffer: string,
): { events: AnalyzeStreamEvent[]; rest: string } => {
  const events: AnalyzeStreamEvent[] = [];
  const chunks = buffer.split("\n\n");
  const rest = chunks.pop() ?? "";

  for (const chunk of chunks) {
    const lines = chunk
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("data:"));

    if (!lines.length) {
      continue;
    }

    const payload = lines.map((line) => line.slice(5).trim()).join("\n");
    if (!payload) {
      continue;
    }

    try {
      const parsedEvent: AnalyzeStreamEvent = JSON.parse(
        payload,
      ) as AnalyzeStreamEvent;
      events.push(parsedEvent);
    } catch {
      // Ignore malformed chunks from stream boundaries.
    }
  }

  return { events, rest };
};

class AnalysisService {
  async analyze(
    payload: AnalyzeImageRequest,
    onEvent?: (event: AnalyzeStreamEvent) => void,
  ): Promise<AnalyzeImageResponse> {
    const formData = new FormData();
    formData.append("file", payload.image, "capture.jpg");

    let streamBuffer = "";
    let processedLength = 0;
    let finalEvent: AnalyzeImageResponse | null = null;

    const handleChunk = (rawChunk: string): void => {
      if (!rawChunk) {
        return;
      }

      streamBuffer += rawChunk;
      const { events, rest } = parseSseEvents(streamBuffer);
      streamBuffer = rest;

      for (const event of events) {
        onEvent?.(event);
        if (event.type === "result") {
          finalEvent = event;
        }
      }
    };

    const config: CustomAxiosRequestConfig = {
      responseType: "text",
      headers: {
        Accept: "text/event-stream",
      },
      disableGlobalError: true,
      onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
        const xhr = progressEvent.event?.target as XMLHttpRequest | undefined;
        const responseText = xhr?.responseText ?? "";

        if (!responseText || responseText.length <= processedLength) {
          return;
        }

        const chunk = responseText.slice(processedLength);
        processedLength = responseText.length;
        handleChunk(chunk);
      },
    };

    const { data } = await api.post<string>(
      "/api/nameplate/analyze",
      formData,
      config,
    );

    if (typeof data === "string" && data.length > processedLength) {
      const remainingChunk = data.slice(processedLength);
      handleChunk(remainingChunk);
    }

    if (!finalEvent) {
      throw new Error("No result event received from analysis stream");
    }

    return finalEvent;
  }

  async submitValidation(payload: SubmitValidationRequest): Promise<void> {
    await api.post("/api/nameplate/validate", payload);
  }

  async submitManualSelection(
    payload: SubmitManualSelectionRequest,
  ): Promise<void> {
    await api.post("/api/nameplate/manual-selection", payload);
  }

  async submitPricingChoice(
    payload: SubmitPricingChoiceRequest,
  ): Promise<void> {
    await api.post("/api/nameplate/pricing-choice", payload);
  }

  async submitRepairRequest(payload: SubmitRepairRequest): Promise<void> {
    const formData = new FormData();
    formData.append("analysis", JSON.stringify(payload.analysis));
    formData.append("pricing_choice", payload.pricing_choice);
    formData.append("issue", payload.issue);
    formData.append("enterprise_name", payload.enterprise_name);
    formData.append("contact_name", payload.contact_name);
    formData.append("contact_email", payload.contact_email);
    formData.append("contact_phone", payload.contact_phone);
    formData.append("address", payload.address);
    formData.append("access_instructions", payload.access_instructions);
    formData.append("email", payload.email);
    formData.append("email_copy", payload.email_copy);
    formData.append("photo", payload.photo, "nameplate-capture.jpg");

    await api.post("/api/nameplate/repair-request", formData);
  }
}

export default new AnalysisService();
